import { BehaviorSubject, map } from 'rxjs';
import {
  CustomFieldProps,
  DynamicField,
  DynamicForm,
  FieldVariant,
  NumberFieldProps,
  SelectFieldProps,
  SelectOption,
  TextareaFieldProps,
  TextFieldProps,
} from '@/components/form-builder/form-builder.types';
import { DynaFieldType } from '@/types';
import { FormArray } from '@angular/forms';
import { FieldDetailTableComponent } from '../field-detail-table/field-detail-table.component';

interface SystemType {
  value: string;
  label: string;
}

type SystemSubType = Record<string, SystemType[]>;

export interface FieldEditorState {
  label$: BehaviorSubject<string>;
  name$: BehaviorSubject<string>;
  description$: BehaviorSubject<string>;
  type$: BehaviorSubject<SelectOption | undefined>;
  status$: BehaviorSubject<SelectOption | undefined>;
  order$: BehaviorSubject<number>;
  source$: BehaviorSubject<SelectOption | undefined>;
  sourceField$: BehaviorSubject<SelectOption | undefined>;
  currentType$: BehaviorSubject<DynaFieldType | null>;
  detailsFormArray: FormArray;
}

export interface FieldEditorFormObjectProps {
  state: FieldEditorState;
  statusOptions: SelectOption[];
  fieldTypeOptions: SelectOption[];
  systemTypeOptions: SelectOption[];
  columnTypeOptions: SelectOption[];
  systemSubTypes: SystemSubType;
  onTypeChange: () => void;
  onAddDetail: () => void;
  onRemoveDetail: (index: number) => void;
}

export const getFieldEditorFormObject = ({
  state,
  statusOptions,
  fieldTypeOptions,
  systemTypeOptions,
  columnTypeOptions,
  systemSubTypes,
  onTypeChange,
  onAddDetail,
  onRemoveDetail,
}: FieldEditorFormObjectProps): DynamicForm => {
  const currentSystemSubTypeOptions$ = state.source$.pipe(
    map(source => {
      if (!source?.code) return [];
      const key = source.code as string;
      return (systemSubTypes[key] || []).map(st => ({ name: st.label, code: st.value }));
    })
  );

  const labelField: DynamicField<TextFieldProps> = {
    label: 'dynamicDocument.form.label',
    variant: FieldVariant.TEXT,
    isRequired: true,
    props: {
      placeholder: 'dynamicDocument.form.placeholders.label',
      value: state.label$.asObservable(),
      onChange: (value: string) => state.label$.next(value),
    },
  };

  const nameField: DynamicField<TextFieldProps> = {
    label: 'dynamicDocument.form.name',
    variant: FieldVariant.TEXT,
    isRequired: true,
    props: {
      placeholder: 'dynamicDocument.form.placeholders.name',
      value: state.name$.asObservable(),
      onChange: (value: string) => state.name$.next(value),
    },
  };

  const statusField: DynamicField<SelectFieldProps> = {
    label: 'dynamicDocument.form.status',
    variant: FieldVariant.SELECT,
    isRequired: true,
    props: {
      placeholder: 'dynamicDocument.form.placeholders.status',
      value: state.status$.asObservable(),
      onSelectChange: (option: SelectOption) => state.status$.next(option),
      options: statusOptions,
    },
  };

  const orderField: DynamicField<NumberFieldProps> = {
    label: 'dynamicDocument.form.position',
    variant: FieldVariant.NUMBER,
    isRequired: true,
    props: {
      placeholder: 'dynamicDocument.form.placeholders.position',
      value: state.order$.asObservable(),
      onChange: (value: number) => state.order$.next(value),
    },
  };

  const typeField: DynamicField<SelectFieldProps> = {
    label: 'dynamicDocument.form.type',
    variant: FieldVariant.SELECT,
    isRequired: true,
    props: {
      placeholder: 'dynamicDocument.form.placeholders.type',
      value: state.type$.asObservable(),
      onSelectChange: (option: SelectOption) => {
        state.type$.next(option);
        state.currentType$.next(option?.code as DynaFieldType);
        onTypeChange();
      },
      options: fieldTypeOptions,
    },
  };

  const descriptionField: DynamicField<TextareaFieldProps> = {
    label: 'dynamicDocument.form.description',
    variant: FieldVariant.TEXTAREA,
    props: {
      value: state.description$.asObservable(),
      onChange: (value: string) => state.description$.next(value),
      rows: 3,
    },
  };

  const sourceField: DynamicField<SelectFieldProps> = {
    label: 'dynamicDocument.form.nameSource',
    variant: FieldVariant.SELECT,
    isRequired: true,
    isHidden: state.currentType$.pipe(map(t => t !== DynaFieldType.SYSTEM)),
    props: {
      placeholder: 'dynamicDocument.form.placeholders.nameSource',
      value: state.source$.asObservable(),
      onSelectChange: (option: SelectOption) => {
        state.source$.next(option);
        state.sourceField$.next(undefined);
      },
      options: systemTypeOptions,
    },
  };

  const sourceFieldField: DynamicField<SelectFieldProps> = {
    label: 'dynamicDocument.form.fields',
    variant: FieldVariant.SELECT,
    isRequired: true,
    isHidden: state.currentType$.pipe(map(t => t !== DynaFieldType.SYSTEM)),
    props: {
      value: state.sourceField$.asObservable(),
      onSelectChange: (option: SelectOption) => state.sourceField$.next(option),
      options: currentSystemSubTypeOptions$,
    },
  };

  const isTableOrList$ = state.currentType$.pipe(map(t => t !== DynaFieldType.TABLE && t !== DynaFieldType.LIST));

  const detailTableField: DynamicField<CustomFieldProps> = {
    label: '',
    variant: FieldVariant.CUSTOM,
    isHidden: isTableOrList$,
    props: {
      component: FieldDetailTableComponent,
      injectKeys: ['detailsFormArray', 'isTableType', 'isListType', 'columnTypeOptions', 'addDetail', 'removeDetail'],
      detailsFormArray: state.detailsFormArray,
      isTableType: true,
      isListType: true,
      columnTypeOptions,
      addDetail: { emit: () => onAddDetail() },
      removeDetail: { emit: (index: number) => onRemoveDetail(index) },
    },
  };

  return {
    title: '',
    class: 'mx-2',
    isHeaderHidden: true,
    grids: [
      {
        title: '',
        isHeaderHidden: true,
        gridItems: [
          { fields: [labelField, nameField] },
          { fields: [statusField, orderField] },
          { fields: [typeField] },
          { fields: [descriptionField] },
          { fields: [sourceField, sourceFieldField] },
          { fields: [detailTableField] },
        ],
      },
    ],
  };
};
